import { useEffect, useRef, useState } from 'react';
import { apiPost } from '../utils/api';
import { useDebouncedValue, useLatestRequest } from './useSearchRequest';

/**
 * THE LIVE COST PREVIEW — ask the SERVER what the draft costs, and print what
 * it says.
 *
 * ══════════════════ WHY THIS IS A REQUEST AND NOT ARITHMETIC ══════════════
 * Owner: *"updating values in the expanded details section should live update
 * the totals ... Currently I update computer hire and no prices change."*
 *
 * The obvious fix is ten lines of browser arithmetic. It is the wrong fix.
 * `client/src/components/budget/CLAUDE.md`'s rule is *the server computes the
 * money; this directory reports it*, and it exists because FOUR duplicated
 * cost formulas in this subsystem produced a live production bug that
 * understated wages. A client-side preview would be a FIFTH — and the one a
 * user watches while typing.
 *
 * 🔻 THERE WAS ONE CLIENT-COMPUTED PREVIEW LEFT AND IT IS NOW GONE TOO
 * (2026-08-23). The Gantt's drag preview was `weeks × rate` while day-model
 * labour is `days / 5 × rate`; §4 of
 * `server/tests/regressions/crew-cost-path-parity-manifest.test.js` recorded
 * that it over- or under-states, and ACCEPTED it because the figure was
 * transient. Batching the drag made it stand until Save, the owner reported it,
 * and the answer was to give the Gantt strip its own instance of THIS HOOK
 * rather than to improve the arithmetic. **There is no client-computed money
 * preview anywhere in `client/src` now, and adding one needs an argument
 * neither of the two that existed could make.**
 *
 * So this hook POSTs the draft to `/api/budget/crew-members/:id/cost-preview`,
 * which runs `buildCrewMemberCostFields` — the SAME assembly
 * `GET /crew-members` runs — and returns the SAME keys the panel already
 * prints. The client sends and prints; it does not calculate. There is no
 * arithmetic in this file.
 *
 * ══════════════════ THE THREE THINGS THAT GO WRONG ══════════════════
 *
 * 1. 🚨 A REQUEST PER KEYSTROKE IS NOT ACCEPTABLE ON A MONEY ENDPOINT, so the
 *    draft is debounced. See `CREW_COST_PREVIEW_DEBOUNCE_MS` for the number
 *    and the reasoning.
 *
 * 2. 🚨 A DEBOUNCE IS NOT A STALE-RESPONSE GUARD, and treating it as one is
 *    what made four of six search surfaces in this codebase look safe while
 *    being broken (`hooks/useSearchRequest.js`). Debouncing narrows the window
 *    in which two requests overlap; it never closes it. So this ALSO uses
 *    `useLatestRequest`, and `isCurrent()` gates every `setState` INCLUDING
 *    the `catch` and the `finally`.
 *    🔑 It matters more here than on a search box: a stale figure is not a
 *    wrong list, it is a wrong PRICE, presented as current, with no way for
 *    the reader to tell. A preview showing the previous value while the user
 *    types is worse than no preview at all.
 *
 *    🕶️ AND THE TWO GUARDS BELOW ARE REDUNDANT — MEASURED, AND SAID HERE
 *    RATHER THAN LEFT TO LOOK LIKE TWO INDEPENDENT PROTECTIONS. Each request
 *    lives inside an effect keyed on the debounced body, so React's own
 *    cleanup sets `cancelled` on the previous closure before the next request
 *    starts; `isCurrent()` then has nothing left to catch. Proven by mutation
 *    on 2026-08-23: removing EITHER one alone leaves §2 of
 *    `useCrewCostPreview.test.js` fully green, and only removing BOTH reds it
 *    (the stale £10 overwrites the fresh £9,990, and a stale rejection wipes a
 *    good preview to null).
 *
 *    🔑 BOTH ARE KEPT ANYWAY, and the reason is that the sufficiency of
 *    `cancelled` is a property of THIS hook's SHAPE, not of the problem. Hoist
 *    the fetch out of the effect, add a manual "refresh preview" control, or
 *    fire one from an event handler, and the cleanup stops firing between
 *    requests while `isCurrent()` keeps working. It is the house-declared
 *    shape for this hazard and it costs nothing. **Do not delete one because a
 *    test still passes without it** — that is the measurement above, not a
 *    licence.
 *
 * 3. 🚨 A FAILED PREVIEW MUST NOT PRINT A NUMBER. On any failure the preview
 *    is CLEARED, so the panel falls back to the last SAVED figures — which are
 *    true, merely out of date — and drops the provisional marker with them.
 *    It never keeps a stale preview and never invents one.
 *    🪤 Note what "cleared" means: `null`, so the panel reads `member` again.
 *    Returning `{}` would hand the panel a member-shaped object full of
 *    `undefined`, and `Number(undefined)` is `NaN` while `Number(null)` is
 *    `0` — the browser check on 2026-08-22 found absent fields printing as
 *    £0.00 and that is exactly how.
 *
 * ══════════════════ WHAT THE CALLER OWES, AND WHAT IT MUST NOT DO ══════
 * 🔻 THE "IT PREVIEWS THE MEMBER FORM ONLY" PARAGRAPH IS SUPERSEDED. It said
 * the day set and the week allocations *"have their own editors … the endpoint
 * reads those from the database as stored"*, and drew the honest consequence
 * that labour lagged a date change. The ENDPOINT took a draft `days` array and
 * `week_overrides` on 2026-08-23, so the caller now sends them and the lag is
 * gone. This hook is unchanged by that: it posts whatever body it is given.
 *
 * 🚨 **THE BODY MUST BE THE SAVE'S BODY.** That is the whole of *"the preview
 * is what you will get"* — it is a property of what the CALLER assembles, not
 * of anything in this file. Two callers exist and each mirrors its own save:
 * `pages/BudgetCostCodes.js`'s edit preview sends `buildMemberPutPayload`, and
 * its Gantt-strip preview sends the strip's dates + `buildDayPayload` +
 * the merged week overrides. **A third caller that assembles a body by hand is
 * a third answer to "what does this draft cost".**
 *
 * 🪤 **ONE LIVE INSTANCE PER MEMBER.** Two previews for one person is the
 * "which figure is on screen" question this whole design exists to have one
 * answer to; the strip's instance stands down on OWNERSHIP when the details
 * panel owns the member, which is the same key the Save and the exit guard use.
 */

/**
 * 🕐 400ms, and this is a JUDGEMENT the owner will FEEL — jsdom cannot tell
 * anyone whether a live update feels responsive, so this number is offered
 * rather than proven.
 *
 * Why not the shared 250ms default: these are CURRENCY fields, so a user types
 * a whole number (`1250`) rather than pausing between characters the way they
 * do in a search box. At 250ms a four-digit entry typically fires two or three
 * costings of half-typed numbers; at 400ms it is usually one, of the number
 * they meant. Why not higher: past roughly half a second the panel stops
 * reading as live and starts reading as laggy, which loses the whole point.
 *
 * Exported so a test can drive it and so the owner's answer, when he gives
 * one, is a one-line change rather than a hunt.
 */
export const CREW_COST_PREVIEW_DEBOUNCE_MS = 400;

/**
 * @param {Object}   opts
 * @param {number}   opts.memberId   the crew member being edited, or null.
 * @param {Object}   opts.draftBody  the request body — pass the SAME object
 *   `buildMemberPutPayload` builds for the Save. 🔑 That is what makes "the
 *   preview is what you will get" a structural property rather than a hope:
 *   the two requests then differ in their URL and in nothing else.
 * @param {boolean}  opts.enabled    false while the panel is closed, the user
 *   cannot edit, or nothing has been changed yet — no request is issued and
 *   any existing preview is dropped.
 * @param {number}   [opts.delayMs]
 * @param {number}   [opts.maxWaitMs] optional ceiling on how long a CONTINUOUSLY
 *   changing draft may go uncosted (2026-10-06, owner: the Gantt drag's footer
 *   should *"change live"*). A drag moves the dates every few pixels, so a pure
 *   debounce never fires until the pointer stops; with `maxWaitMs` the latest
 *   draft is costed at least that often. Still at most one request per window —
 *   a throttle on top of the debounce, never a request per change. Omitted (the
 *   panel's instance) → behaviour unchanged.
 * @returns {{ preview: Object|null, pending: boolean, failed: boolean }}
 *   `preview` is the server's field object, or `null` for "print the saved
 *   figures". `pending` is true while a costing is in flight or debouncing.
 */
export function useCrewCostPreview({
  memberId, draftBody, enabled, delayMs = CREW_COST_PREVIEW_DEBOUNCE_MS, maxWaitMs = null,
}) {
  const [preview, setPreview] = useState(null);
  const [pending, setPending] = useState(false);
  const [failed, setFailed] = useState(false);
  const begin = useLatestRequest();

  /* 🔑 THE ID AND THE BODY ARE ONE VALUE. The debounced key is the
     SERIALISED `{ memberId, body }` PAIR, not the body alone, because the
     question this hook asks the server is a single one — *"cost THIS body for
     THIS member"* — and its two halves must move together or they can
     disagree.

     🚨 THE DEFECT THIS SHAPE CLOSES, found by an adversarial money sweep on
     2026-08-23 and LIVE ON STAGING. The body was debounced and the member id
     was NOT, and both were dependencies of the request effect below. So for up
     to `delayMs` after the edited member changed, the effect re-fired with the
     NEW id and the PREVIOUS member's serialised draft, and
     `POST /crew-members/8/cost-preview` went out carrying member 7's rate
     override, allowances, caps, grade id and day set. It returned **200** — a
     grade binds fine within one department, so nothing refuses it — and the
     answer was printed as member 8's cost summary and in the sticky footer
     total. One person's money under another's name, with nothing on screen to
     say so.
     🪤 THE MEMBER-CHANGE CLEAR BELOW DOES NOT COVER IT, and reaching for a
     third guard is the wrong move. That one drops the stale FIGURE; this defect
     is a FRESH figure computed from mismatched inputs, which no amount of
     clearing can catch. Pairing the two values is what makes the mismatch
     unrepresentable.

     An edit that changes nothing the money depends on (a note, a location)
     still produces an identical key and still fires no request at all.
     🪤 `JSON.stringify` is stable here because `buildMemberPutPayload` builds
     its object with literal keys in a fixed order, and the wrapper adds two
     literal keys of its own — it is not a general-purpose deep compare and must
     not be treated as one. */
  const costingRequest = enabled && memberId != null && draftBody
    ? JSON.stringify({ memberId, body: draftBody })
    : null;
  const settledRequest = useDebouncedValue(costingRequest, delayMs);

  /* ⏱️ `maxWaitMs`: while the key keeps changing (it differs from the settled
     one), cost the LATEST key once `maxWaitMs` has passed since the window
     opened, then open a new window. `forcedRequest` is dropped the moment the
     debounce settles, so it can never outrank a newer settled key. */
  const [forcedRequest, setForcedRequest] = useState(null);
  const windowStart = useRef(null);
  useEffect(() => { setForcedRequest(null); }, [settledRequest]);
  useEffect(() => {
    if (!maxWaitMs || costingRequest == null || costingRequest === settledRequest) {
      windowStart.current = null;
      return undefined;
    }
    if (windowStart.current == null) windowStart.current = Date.now();
    const wait = Math.max(0, maxWaitMs - (Date.now() - windowStart.current));
    const t = setTimeout(() => {
      windowStart.current = Date.now();
      setForcedRequest(costingRequest);
    }, wait);
    return () => clearTimeout(t);
  }, [costingRequest, settledRequest, maxWaitMs]);
  const debouncedRequest = (forcedRequest != null && costingRequest !== settledRequest)
    ? forcedRequest
    : settledRequest;

  /* `pending` has to go true the moment the user types — on the RAW key, not
     the debounced one — or the marker appears only after the delay has already
     elapsed and the figures are about to change anyway. */
  useEffect(() => {
    if (costingRequest == null) { setPending(false); return; }
    if (costingRequest !== debouncedRequest) setPending(true);
  }, [costingRequest, debouncedRequest]);

  /* 🔑 Drop everything when the panel closes or the edited member changes. A
     preview that outlived its member would print one person's money under
     another's name. */
  const lastMemberId = useRef(memberId);
  useEffect(() => {
    if (lastMemberId.current !== memberId) {
      lastMemberId.current = memberId;
      setPreview(null);
      setFailed(false);
    }
  }, [memberId]);

  useEffect(() => {
    if (debouncedRequest == null) {
      setPreview(null);
      setPending(false);
      setFailed(false);
      return;
    }

    /* 🔑 THE URL AND THE BODY COME OUT OF THE SAME VALUE. `memberId` is
       deliberately NOT read here and is deliberately NOT a dependency — reading
       the live prop is exactly what let a new id be paired with an old body. */
    const { memberId: costingMemberId, body } = JSON.parse(debouncedRequest);

    const isCurrent = begin();
    let cancelled = false;

    (async () => {
      try {
        const res = await apiPost(
          `/api/budget/crew-members/${costingMemberId}/cost-preview`,
          body,
        );
        const data = await res.json();
        // 🚨 `isCurrent()` before EVERY setState, this one included: a slow
        // earlier costing landing after a fast later one would repaint the
        // panel with the figures for a draft the user has already moved past.
        if (cancelled || !isCurrent()) return;
        if (!res.ok) {
          // A refused draft (a foreign grade, a half-week allocation, a
          // locked version) is a FAILURE, not a figure. Fall back to saved.
          setPreview(null);
          setFailed(true);
        } else {
          setPreview(data);
          setFailed(false);
        }
      } catch {
        // 🚨 The catch is gated too. A stale rejection clearing a fresh
        // preview is the same defect as a stale success overwriting one.
        if (cancelled || !isCurrent()) return;
        setPreview(null);
        setFailed(true);
      } finally {
        // 🚨 And the finally. A stale one clears the marker while the real
        // request is still open, so the panel reads as settled on figures
        // that are about to change.
        if (!cancelled && isCurrent()) setPending(false);
      }
    })();

    return () => { cancelled = true; };
    // `begin` is stable (a ref-backed callback from useLatestRequest).
  }, [debouncedRequest, begin]);

  return { preview, pending, failed };
}
